import React, { createContext, useCallback, useContext, useState } from 'react'

import { Modal, Row, Col, Input, Select } from 'antd'
import { useSelector } from 'react-redux'
import { isFunction } from 'lodash'
import { faAt, faGlobe, faIdBadge, faIdCard, faLocationArrow, faPhone } from '@fortawesome/pro-solid-svg-icons'

import { School, SCHOOL_VALIDATORS } from '../../utils/entities/school'
import { selectUser } from '../../reducers/UserReducer'
import { GlobalContext } from '../GlobalProvider'
import { isValid, ValidationFields } from '../../utils/validators'
import { isObject } from '../../utils'
import FormItem from '../../Components/shared/Forms/FormItem'
import { createSchool } from '../../utils/api/school'
import { onError, onSuccess, resolvePromises } from '../../utils/apiHelper'
import { addSchoolSection } from '../../utils/api/schoolSection'
import { getFullAbbreviation } from '../../utils/entities/schoolSection'
import BulkSchoolSectionForm from '../../Components/shared/SchoolSectionsDrawer/BulkSchoolSectionForm'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { Properties } from '../../utils/entities'

export const SchoolModalContext = createContext()

const { ABBREVIATION, ADDRESS, COUNTRY, EMAIL, NAME, PHONE_NUMBER } = Properties

const DEFAULT_FORM_HANDLER = { data: null, callback: null }

const SchoolModal = ({ children }) => {
  const { countries } = useContext(GlobalContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [formHandler, setFormHandler] = useState(DEFAULT_FORM_HANDLER)
  const [newSchoolSections, setNewSchoolSections] = useState([])
  const [error, setError] = useState({ missingFields: [] })

  const handleClose = useCallback(school => {
    setFormHandler(DEFAULT_FORM_HANDLER)
    setNewSchoolSections([])

    if (isFunction(formHandler.callback)) {
      formHandler.callback(school)
    }
  }, [formHandler.callback])
  const setData = useCallback((key, value) => {
    setFormHandler(prev => ({ ...prev, data: { ...prev.data, [key]: value } }))
  }, [])

  const handleSubmit = useCallback(() => {
    const school = new School(formHandler.data).trimmed()

    const errors = isValid(school, SCHOOL_VALIDATORS)

    if (errors.keys.length === 0) {
      const parameters = school.toRequestBody()

      createSchool(user, parameters).then(json => {
        if (json?.data) {
          onSuccess(t('school_form_modal.create.success'))

          if (newSchoolSections.length > 0) {
            const promisesStack = []

            newSchoolSections.forEach((section, index) => {
              section.school = json.data.id

              promisesStack.push(
                addSchoolSection(user, section.toRequestBody()).then(response => {
                  if (response?.data) {
                    onSuccess(t('school_section.create.success.verbose', { name: getFullAbbreviation(section) }))
                    return response.data
                  } else {
                    return null
                  }
                }).catch(() => {
                  onError(t('school_section.create.error.verbose', { name: getFullAbbreviation(section) }))

                  return null
                })
              )
            })

            resolvePromises(promisesStack).then(sections => {
              json.data.sections = [...json.data.sections, ...sections.filter(s => s !== null)]

              handleClose(json.data)
            })
          } else {
            handleClose(json.data)
          }
        }
      }).catch(e => {
        onError(t('school_form_modal.create.error'))
      })
    } else {
      const details = {}

      Object.keys(errors.messages).forEach(key => {
        details[t(key)] = errors.messages[key].map(m => t(m))
      })

      setError(error => ({
        ...error,
        title: t('form.errors'),
        details,
        visible: true,
        missingFields: errors.keys
      }))
    }
  }, [t, user, formHandler.data, newSchoolSections, handleClose])

  const errorClass = useCallback(error.missingFields.reduce((acc, f) => {
    acc[f] = 'error-ant-border'

    return acc
  }, {}), [error])

  return (
    <>
      <SchoolModalContext.Provider value={{ formHandler, setFormHandler }}>
        {children}
      </SchoolModalContext.Provider>
      <Modal
        width={1000}
        visible={isObject(formHandler.data)}
        onCancel={handleClose}
        onOk={handleSubmit}
        title={t('school_form_modal.title')}
        cancelText={t('modal.cancel_button')}
        okText={t('modal.ok_button.create')}
        okButtonProps={{
          disabled: (
            formHandler.data === null ||
            isValid(new School(formHandler.data).trimmed(), SCHOOL_VALIDATORS).keys.length > 0 ||
            newSchoolSections.length === 0
          )
        }}
      >
        {formHandler.data && (
          <Row>
            <Col span={12}>
              <h3 className='pdl-12'> {t('common.school')}</h3>
              <div className='pd-12'>
                <FormItem icon={faIdCard} label='common.name.dd' required>
                  <Input
                    className={errorClass[ValidationFields.NAME]}
                    value={formHandler.data.name ?? null}
                    onChange={e => setData(NAME, e.target.value)}
                  />
                </FormItem>
                <FormItem icon={faIdBadge} label='common.acronym.dd' required>
                  <Input
                    className={errorClass[ValidationFields.ACRONYM]}
                    value={formHandler.data.abbreviation ?? null}
                    onChange={e => setData(ABBREVIATION, e.target.value)}
                  />
                </FormItem>
                <FormItem icon={faLocationArrow} label='common.address.dd'>
                  <Input
                    value={formHandler.data.address ?? null}
                    onChange={e => setData(ADDRESS, e.target.value)}
                  />
                </FormItem>
                <FormItem icon={faGlobe} label='common.country.dd' className='form-select'>
                  <Select
                    value={formHandler.data.country?.id ?? null}
                    onChange={value => setData(COUNTRY, { id: value })}
                  >
                    {countries.map(c => (
                      <Select.Option key={c.id} value={c.id}>
                        {t('country.' + c.code)}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
                <FormItem icon={faAt} label='common.email.dd'>
                  <Input
                    value={formHandler.data.email ?? null}
                    onChange={e => setData(EMAIL, e.target.value)}
                  />
                </FormItem>
                <FormItem icon={faPhone} label='common.phone_number.dd'>
                  <Input
                    value={formHandler.data.phoneNumber ?? null}
                    onChange={e => setData(PHONE_NUMBER, e.target.value)}
                  />
                </FormItem>
              </div>
            </Col>
            <Col span={12}>
              <h3 className='pdl-12'> {t('common.sections')}</h3>
              <div style={{ height: '420px' }}>
                <BulkSchoolSectionForm
                  useFooter={false}
                  validators
                  onChange={setNewSchoolSections}
                />
              </div>
            </Col>
          </Row>
        )}
      </Modal>
    </>
  )
}

export default SchoolModal
